import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Alert, Button, Card, Form, Input, Select, Typography } from "antd";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DATETIME_PICKER_FORMAT } from "@/config";
import type { InvoiceSearchParams } from "@/entity/purchasing/invoice";
import { SORT } from "@/meta/common";
import { INVOICE_TYPE } from "@/meta/purchase/invoice";
import { dateTimeFormat } from "@/utils";

export interface InvoiceManagementRow {
    id?: number | string;
    projectName?: string;
    type?: number | string;
    applyEnterprise?: { name?: string; realName?: string };
    invoiceEnterprise?: { name?: string; realName?: string };
    amount?: number | string;
    createdTime?: string;
}

export interface InvoiceManagementQuery {
    page: number;
    size: number;
    keyword?: string;
    type?: number;
    unitName?: string;
    sortKey?: string;
    sortOrder?: string;
}

export interface InvoiceManagementPageResult {
    list?: InvoiceManagementRow[];
    total?: number;
}

export interface InvoiceManagementPageProps {
    loadInvoices?: (query: InvoiceManagementQuery) => Promise<InvoiceManagementPageResult>;
}

const defaultQuery: InvoiceManagementQuery = { page: 1, size: 10 };

/** 复用 Vue 发票管理的 `/admin/purchase-invoice/paging` 分页接口。 */
export async function loadInvoiceManagement(query: InvoiceManagementQuery): Promise<InvoiceManagementPageResult> {
    const params: InvoiceSearchParams = {
        page: String(query.page),
        size: String(query.size),
        ...(query.keyword ? { keyword: query.keyword } : {}),
        ...(query.type == null ? {} : { type: query.type }),
        ...(query.unitName ? { unitName: query.unitName } : {}),
        ...(query.sortKey ? { sortKey: query.sortKey } : {}),
        ...(query.sortOrder ? { sortOrder: query.sortOrder } : {})
    };
    const { data } = await request.service.get<InvoiceManagementPageResult>("/admin/purchase-invoice/paging", {
        params
    });
    return data;
}

/** 将企业对象转换为 Vue 列表中的单位名称。 */
function getEnterpriseName(enterprise?: InvoiceManagementRow["applyEnterprise"]): string {
    return enterprise?.name || enterprise?.realName || "--";
}

/** 保持金额展示与 Vue `formatNumber(amount, 2)` 的两位小数契约一致。 */
function formatAmount(value?: number | string): string {
    if (value == null || value === "") return "--";
    const amount = Number(value);
    return Number.isFinite(amount) ? amount.toFixed(2) : "--";
}

/** 将旧 Vue 发票列表创建时间统一格式化到分钟。 */
function formatCreatedTime(value?: string): string {
    return value ? dateTimeFormat(value, DATETIME_PICKER_FORMAT) : "--";
}

/** 保持发票列表的筛选值与 Vue ProTable 提交参数一致。 */
function buildQuery(values: { keyword?: string; type?: number; unitName?: string }): InvoiceManagementQuery {
    return {
        ...defaultQuery,
        ...(values.keyword?.trim() ? { keyword: values.keyword.trim() } : {}),
        ...(values.type == null ? {} : { type: Number(values.type) }),
        ...(values.unitName?.trim() ? { unitName: values.unitName.trim() } : {})
    };
}

/** 采购发票管理列表：覆盖 Vue 首屏查询、筛选、分页、详情入口、空态和失败反馈。 */
export function InvoiceManagementPage({ loadInvoices = loadInvoiceManagement }: InvoiceManagementPageProps) {
    const [filterForm] = Form.useForm();
    const [query, setQuery] = useState<InvoiceManagementQuery>(defaultQuery);
    const [rows, setRows] = useState<InvoiceManagementRow[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // 用 zhihao-ui-react 列契约承接 Vue 发票列表的链接、枚举、金额和日期展示。
    const columns: TableColumnProps<InvoiceManagementRow>[] = [
        {
            key: "id",
            label: "编号",
            minWidth: 138,
            prop: "id",
            render: ({ row, cellValue }) =>
                row.id == null ? (
                    String(cellValue || "--")
                ) : (
                    <Link to={`/purchasingManage/invoiceManagement/detail/${row.id}`}>{String(cellValue)}</Link>
                )
        },
        {
            key: "projectName",
            label: "项目",
            minWidth: 228,
            prop: "projectName",
            render: ({ cellValue }) => String(cellValue || "--")
        },
        {
            key: "type",
            label: "类型",
            minWidth: 165,
            prop: "type",
            render: ({ cellValue }) =>
                cellValue == null ? "--" : String(INVOICE_TYPE.getIntroByCode(Number(cellValue)) || "--")
        },
        {
            key: "applyEnterprise",
            label: "申请单位",
            minWidth: 228,
            prop: "applyEnterprise",
            render: ({ row }) => getEnterpriseName(row.applyEnterprise)
        },
        {
            key: "invoiceEnterprise",
            label: "开票单位",
            minWidth: 214,
            prop: "invoiceEnterprise",
            render: ({ row }) => getEnterpriseName(row.invoiceEnterprise)
        },
        {
            key: "amount",
            label: "开票金额 (元)",
            minWidth: 130,
            prop: "amount",
            render: ({ cellValue }) => formatAmount(cellValue as number | string | undefined)
        },
        {
            key: "createdTime",
            label: "创建时间",
            minWidth: 150,
            prop: "createdTime",
            sortable: true,
            render: ({ cellValue }) => formatCreatedTime(cellValue as string | undefined)
        }
    ];

    useEffect(() => {
        let disposed = false;
        void loadInvoices(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("发票加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadInvoices, query]);

    // 将 zhihao-ui-react 排序方向映射为旧 Vue 发票接口的 sortKey/sortOrder，并重置页码。
    const handleSortChange = ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
        const nextQuery = { ...query, page: 1 };
        delete nextQuery.sortKey;
        delete nextQuery.sortOrder;
        if (prop === "createdTime" && order) {
            nextQuery.sortKey = "created_time";
            nextQuery.sortOrder = SORT.getIntroByCode(order);
        }
        setQuery(nextQuery);
    };

    return (
        <section aria-label="invoice-management-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<InvoiceManagementRow>
                    data={rows}
                    columns={columns}
                    pagination={false}
                    rowKey={row => String(row.id)}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                发票管理
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="admin-table-filter-form mb-4"
                                form={filterForm}
                                layout="inline"
                                onFinish={values => setQuery(buildQuery(values))}
                            >
                                <Form.Item label="项目" name="keyword">
                                    <Input
                                        aria-label="项目名称或编号"
                                        placeholder="请输入项目名称或编号搜索"
                                        maxLength={32}
                                    />
                                </Form.Item>
                                <Form.Item label="类型" name="type">
                                    <Select
                                        aria-label="发票类型"
                                        allowClear
                                        options={INVOICE_TYPE.options}
                                        placeholder="请选择类型"
                                        style={{ minWidth: 150 }}
                                    />
                                </Form.Item>
                                <Form.Item label="申请单位" name="unitName">
                                    <Input aria-label="单位名称" placeholder="请输入单位名" maxLength={32} />
                                </Form.Item>
                                <Button htmlType="submit" type="primary">
                                    查询
                                </Button>
                                <Button
                                    className="ml-2"
                                    onClick={() => {
                                        filterForm.resetFields();
                                        setQuery(defaultQuery);
                                    }}
                                >
                                    重置
                                </Button>
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                // 对齐旧版 Vue ProTable 的分页条数选项，切换后回到第一页。
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                hideOnSinglePage
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default InvoiceManagementPage;
